import type AbstractShipmentDocument from '@/domains/Abstract/AbstractShipmentDocument';
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import Core from '@/core/Core';
import TransportOrdersDAO from '@/domains/TransportOrders/TransportOrdersDAO';
import TransportOrder from '@/domains/TransportOrders/TransportOrder';
import TransportOrderPosition from '@/domains/TransportOrders/TransportOrderPosition';
import TransportableDocument from '@/domains/TransportableDocuments/TransportableDocument';
import OrderPosition from '@/domains/Orders/OrderPosition';
import Product from '@/domains/Products/Product';
import Assembly from "@/domains/Assemblies/Assembly";
import Shipment from "@/domains/Shipments/Shipment";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import AssembliesService from "@/domains/Assemblies/AssembliesService";
import ShipmentsService from "@/domains/Shipments/ShipmentsService";
import OrdersService from "@/domains/Orders/OrdersService";
import ProductsService from "@/domains/Products/ProductsService";

/**
 * Сервис для ЗТК
 */
class TransportOrdersService {
  static readonly TABS = [
    {id: 'in-work', name: 'В работе', 'isInWork': true },
    {id: 'confirmed', name: 'Утверждённые', 'isConfirmed': true },
    {id: 'finished', name: 'Завершённые', 'isFinished': true },
  ] as const;

  static async get(id: number): Promise<TransportOrder | null> {
    const response = await TransportOrdersDAO.get(id);

    return Core.utils.hydrate(response.data, TransportOrder.fromServer);
  }

  static async getList(params?: Parameters<typeof TransportOrdersDAO.getList>[0]): Promise<TransportOrder[]> {
    const response = await TransportOrdersDAO.getList(params);

    return Core.utils.hydrateArray(response.data, TransportOrder.fromServer);
  }

  static async getListPaginated(
    params?: Parameters<typeof TransportOrdersDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    }
  ): Promise<{
    data: TransportOrder[];
    pagination: RpcDataPagination
  }> {
    const {pagination, sort, ...filters} = params ?? {};

    const response = await TransportOrdersDAO.getList(filters, pagination, sort);

    return {
      data: Core.utils.hydrateArray(response.data, TransportOrder.fromServer),
      pagination: response.meta?.pagination,
    };
  }

  static async add(
    transportOrder: TransportOrder,
    receivers?: any[],
    SWPortalTask?: any,
  ): Promise<boolean> {
    const response = await TransportOrdersDAO.add(transportOrder, receivers, SWPortalTask);

    if (response.ok) {
      Core.showNotification.success('ЗТК создана');
    }

    return response.ok;
  }

  static async update(transportOrder: TransportOrder, updateReasonText?: string): Promise<boolean> {
    const response = await TransportOrdersDAO.update(transportOrder, updateReasonText);

    if (response.ok) {
      Core.showNotification.success('ЗТК обновлена');
    }

    return response.ok;
  }

  /** Удаляет ЗТК с подтверждением. Возвращает true, если удаление произошло. */
  static async deleteWithConfirm(transportOrder: TransportOrder, updateReasonText?: string): Promise<boolean> {
    if (await Core.confirm('Подтверждаете удаление?') === true) {
      const response = await TransportOrdersDAO.delete(transportOrder.id, updateReasonText);

      if (response.ok) {
        Core.showNotification.success('ЗТК удалена');

        transportOrder.isDeleted = true;
      }

      return response.ok;
    }
    else {
      return false;
    }
  }

  /**
   * Загрузить список транспортируемых документов с пагинацией.
   * Используется в реестре через DataGridLoader.
   */
  static async getTransportableDocumentsPaginated(
    params?: Parameters<typeof TransportOrdersDAO.getTransportableDocumentsPaginated>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    }
  ): Promise<{
    data: TransportableDocument[];
    pagination: RpcDataPagination;
  }> {
    const {pagination, sort, ...filters} = params ?? {};

    const response = await TransportOrdersDAO.getTransportableDocumentsPaginated(filters, pagination, sort);

    return {
      data: Core.utils.hydrateArray(response.data, TransportableDocument.fromServer),
      pagination: response.meta?.pagination,
    };
  }

  static async downloadXls(id: number): Promise<void> {
    const response = await TransportOrdersDAO.getXls(id);

    if (!Core.utils.isObject(response.data)) return;

    const {file} = response.data;

    if (file && typeof file.data === 'string' && file.data.length > 0) {
      const blob = Core.utils.file.base64ToBlob(file.data, file.mimeType, file.name);
      Core.utils.file.downloadBlob(blob);
    }
  }

  static async sendNotification(id: number, receivers: any[]): Promise<boolean> {
    const response = await TransportOrdersDAO.sendNotification(id, receivers);

    if (response.ok) {
      Core.showNotification.success('Уведомление отправлено');
    }

    return response.ok;
  }

  /**
   * Параметры запроса для активной вкладки.
   */
  static getRequestParamsByTab(activeTab: {
    id: string
  }): Parameters<typeof TransportOrdersService.getListPaginated>[0] {
    switch (activeTab.id) {
      case 'confirmed': {
        return {isConfirmed: true, isFinished: false};
      }
      case 'finished': {
        return {isFinished: true};
      }
      default: {
        // 'in-work' и всё неизвестное — вкладка по умолчанию.
        // isInWork синтетический: DAO разворачивает его в isConfirmed=false + isFinished=false
        return {isInWork: true};
      }
    }
  }

  static async showSingleSenderTransportOrderDialog(documents?: AbstractShipmentDocument | AbstractShipmentDocument[]): Promise<boolean> {
    const transportOrder = await this.createTransportOrder(TransportOrder.Type.SingleSender, documents);
    return await TransportOrdersService.showTransportOrderDialog(transportOrder);
  }

  static async showConsolidatedTransportOrderDialog(documents?: AbstractShipmentDocument | AbstractShipmentDocument[]): Promise<boolean> {
    const transportOrder = await this.createTransportOrder(TransportOrder.Type.Consolidated, documents);
    return await TransportOrdersService.showTransportOrderDialog(transportOrder);
  }

  static async showOrderPositionPickupTransportOrderDialog(documents?: OrderPosition | OrderPosition[]): Promise<boolean> {
    const transportOrder = await this.createTransportOrder(TransportOrder.Type.OrderPositionPickup, documents);
    return await TransportOrdersService.showTransportOrderDialog(transportOrder);
  }

  static async showReturnTransportOrderDialog(documents?: OrderPosition | OrderPosition[]): Promise<boolean> {
    const transportOrder = await this.createTransportOrder(TransportOrder.Type.Return, documents);
    return await TransportOrdersService.showTransportOrderDialog(transportOrder);
  }

  static async showServiceTransportOrderDialog(): Promise<boolean> {
    const transportOrder = await this.createTransportOrder(TransportOrder.Type.Service, undefined);
    return await TransportOrdersService.showTransportOrderDialog(transportOrder);
  }

  static async showInternalTransportOrderDialog(documents?: Product | Product[] | TransportableDocument | TransportableDocument[]): Promise<boolean> {
    const transportOrder = await this.createTransportOrder(TransportOrder.Type.Internal, documents);
    return await TransportOrdersService.showTransportOrderDialog(transportOrder);
  }

  /**
   * Создаёт ЗТК нужного типа и возвращает её. Диалог не открывает.
   * Если переданы документы — загружает полные объекты и наполняет позициями.
   * Если нет — возвращает пустую ЗТК нужного типа.
   */
  private static async createTransportOrder(
    typeId: string,
    documents?:
      | Shipment | Shipment[]
      | Assembly | Assembly[]
      | OrderPosition | OrderPosition[]
      | Product | Product[]
      | TransportOrder | TransportOrder[]
      | TransportableDocument | TransportableDocument[]
  ): Promise<TransportOrder> {

    const transportOrder = TransportOrder.createFromType(typeId);

    if (documents) {
      const docs = Array.isArray(documents) ? documents : [documents];

      for (const doc of docs) {
        let fullDocument: Assembly | Shipment | TransportOrder | OrderPosition | Product | null;

        if (doc instanceof Assembly || doc.isAssembly)                  fullDocument = await AssembliesService.get(doc.id);
        else if (doc instanceof Shipment || doc.isShipment)             fullDocument = await ShipmentsService.get(doc.id);
        else if (doc instanceof TransportOrder || doc.isTransportOrder) fullDocument = await TransportOrdersService.get(doc.id);
        else if (doc instanceof OrderPosition || doc.isOrderPosition)   fullDocument = await OrdersService.getPosition(doc.id);
        else if (doc instanceof Product || doc.isProduct)               fullDocument = await ProductsService.get(doc.id);
        else throw new TypeError('createTransportOrder: неизвестный тип документа');

        if (fullDocument) {
          transportOrder.addPosition(TransportOrderPosition.fromDocument(fullDocument));
        }
      }
    } else {
      // документы не переданы — пустая ЗТК нужного типа
    }

    return transportOrder;
  }


  /* ────────────────────────────────────────
     Открытие карточки ЗТК
     ──────────────────────────────────────── */

  /**
   * Открывает карточку ЗТК. Принимает либо id, либо готовый инстанс —
   * карточка сама разрулит (поддерживает оба варианта).
   * Промис резолвится по закрытию карточки: true — что-то сохранили, false — нет.
   */
  static async showTransportOrderDialog(idOrTransportOrder: number | TransportOrder): Promise<boolean> {
    let props: { id: number } | { transportOrder: TransportOrder };

    if (typeof idOrTransportOrder === 'number') {
      props = { id: idOrTransportOrder };
    } else {
      props = { transportOrder: idOrTransportOrder };
    }

    return Core.dialogs.TransportOrderCard.showAsPromise(props);
  }

  /**
   * Попап смены статуса. target — ячейка реестра; из контекстного меню
   * не передаётся, попап сам встанет на место меню.
   * Промис: true — сохранили (resolve), false — закрыли без сохранения.
   */
  static showStatusPopup(transportOrder: TransportOrder, target: HTMLElement | null = null): Promise<boolean> {
    // если открыт предыдущий, то закрываем, если нет, то ничего не будет
    Core.dialogs.TransportOrderStatusPopup.hideIfOpen();

    return Core.dialogs.TransportOrderStatusPopup.showAsPromise({
      transportOrder,
      target,
    });
  }

  /**
   * Пункты контекстного меню ЗТК для грида реестра и карточки.
   * Грид обновляет список, а карточка перечитывает ЗТК (или закрывается, если её
   * удалили), только если callback пункта вернул true. Пункты, которые ЗТК
   * не меняют (отправка, выгрузки, просмотр), ничего не возвращают.
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (transportOrder: TransportOrder) => { return TransportOrdersService.showTransportOrderDialog(transportOrder.id); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),

      new ContextMenuItem(
        'Смена таможенного статуса',
        'sync_alt',
        (transportOrder: TransportOrder) => { return TransportOrdersService.showStatusPopup(transportOrder); },
      ),

      new ContextMenuItem(
        'Отправить на e-mail',
        'mail',
        (transportOrder: TransportOrder) => { Core.dialogs.SWMailingListCreateDialog.showAsPromise({ transportOrder }); },
        (transportOrder: TransportOrder) => { return transportOrder.getReasonCannotSendNotification(); },
      ),

      new ContextMenuItem(
        'Создать задачу на портале',
        'add_task',
        (transportOrder: TransportOrder) => { return TransportOrdersService.showPortalTaskCreateDialog(transportOrder); },
        (transportOrder: TransportOrder) => { return transportOrder.getReasonCannotCreatePortalTask(); },
        (transportOrder: TransportOrder) => { return transportOrder?.receiver?.id !== Core.sibWellPartner.id; },
      ),

      new ContextMenuItem(
        'Скачать заявку в Excel',
        'download',
        (transportOrder: TransportOrder) => { return TransportOrdersService.downloadXls(transportOrder.id); },
        (transportOrder: TransportOrder) => { return transportOrder.getReasonCannotDownload(); },
      ),

      new ContextMenuItem(
        'История действий',
        'history',
        (transportOrder: TransportOrder) => {
          Core.dialogs.FeedDialog.show({
            entityTypeName: 'transportOrder',
            entityId:       transportOrder.id,
            showAddComment: false,
          });
        },
        (transportOrder: TransportOrder) => { return transportOrder.getReasonCannotShowFeed(); },
      ),

      new ContextMenuItem(
        'Подробно о грузе',
        'read_more',
        (transportOrder: TransportOrder) => { Core.dialogs.CargoUnitsSummaryDialog.showAsPromise({ transportOrder }); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),

      new ContextMenuItem(
        'Удалить',
        'delete',
        (transportOrder: TransportOrder) => { return TransportOrdersService.deleteWithConfirm(transportOrder); },
        (transportOrder: TransportOrder) => { return transportOrder.getReasonCannotDelete(); },
      ),
    ];
  }

  /**
   * Открывает диалог создания задачи на портале SW.
   */
  static async showPortalTaskCreateDialog(transportOrder: TransportOrder): Promise<void> {
    const fullTransportOrder = await TransportOrdersService.get(transportOrder.id);
    if (fullTransportOrder === null || fullTransportOrder === undefined) return;

    Core.dialogs.SWPortalTaskCreateDialog.show({ transportOrder: fullTransportOrder });
  }
}

export default TransportOrdersService;
